import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, CircularProgress, CssBaseline, ThemeProvider, Typography,
} from '@mui/material';
import publicResolveService, { type PublicResolveResponse } from '../../services/publicResolveService';
import { previewTheme } from './PreviewMock';
import LaCartePage from '../../components/m/lacarte/LaCartePage';
import { toLaCarteModel } from '../../components/m/lacarte/laCarteModel';
import PublicMenu, { type PublicMenuPayload } from './PublicMenu';

/**
 * Unified public page at /m/:slug.
 *
 * Calls the backend unified resolver and branches on `kind` — but BOTH kinds
 * now render through the shared La Carte view (approved Direction A design):
 *   - kind === 'wine-list': adapted directly via toLaCarteModel, so existing
 *     wine-list public URLs show the new design without waiting for the
 *     wine-list → menu data migration.
 *   - kind === 'menu':      rendered by PublicMenu (a thin adapter over the
 *     same LaCartePage).
 *
 * The 404/error state is generic on purpose — it must not reveal whether
 * the slug was checked against the menu collection, the wine-list
 * collection, or both. Mirrors the backend's identical-404 discipline.
 */
const PublicM: React.FC = () => {
  // venueDomain is present on the venue-scoped route /m/:venueDomain/:slug
  // (MENU-PUBLIC-URL-SLUGS.1); undefined on the legacy /m/:slug route.
  const { slug, venueDomain } = useParams<{ slug: string; venueDomain?: string }>();
  const [data, setData] = useState<PublicResolveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * 'not-found'  → slug missing, share missing, or underlying record not ACTIVE
   * 'error'      → network / server error
   * null         → no error
   */
  const [error, setError] = useState<'not-found' | 'error' | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('not-found');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    publicResolveService.getBySlug(slug, venueDomain)
      .then(d => {
        // Defensive: route any future or unrecognised resolver kind through the
        // generic "unavailable" path. Prevents crashes or raw-payload exposure
        // if the backend ever introduces a new content kind we don't render here.
        const kind: string = (d as any)?.kind ?? '';
        if (kind !== 'menu' && kind !== 'wine-list') {
          setError('not-found');
          return;
        }
        setData(d);
      })
      .catch(err => {
        if (err?.response?.status === 404) setError('not-found');
        else setError('error');
      })
      .finally(() => setLoading(false));
  }, [slug, venueDomain]);

  return (
    <ThemeProvider theme={previewTheme}>
      <CssBaseline />
      {loading && <CenteredState><CircularProgress size={32} /></CenteredState>}
      {!loading && error === 'not-found' && (
        <CenteredState>
          <Typography
            sx={{
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'primary.main',
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            Unavailable
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
              fontSize: '1.4rem',
              color: 'text.primary',
              textAlign: 'center',
              maxWidth: 360,
              lineHeight: 1.3,
            }}
          >
            This page isn't available right now. It may have been unpublished by the venue.
          </Typography>
        </CenteredState>
      )}
      {!loading && error === 'error' && (
        <CenteredState>
          <Typography sx={{ color: 'text.secondary' }}>
            Couldn't load this page. Please try again.
          </Typography>
        </CenteredState>
      )}
      {!loading && !error && data && data.kind === 'wine-list' && (
        <LaCartePage model={toLaCarteModel('wine-list', data.data)} />
      )}
      {!loading && !error && data && data.kind === 'menu' && (
        <PublicMenu data={data.data as PublicMenuPayload} />
      )}
    </ThemeProvider>
  );
};

const CenteredState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      color: 'text.primary',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    {children}
  </Box>
);

export default PublicM;
